// stereo-core: tolerant delimited-text import for orientation data.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { OrientationFormat, formatToPlane } from "./orient.js";

export interface Table { header: string[] | null; rows: string[][] }
export type Convention = OrientationFormat;
/** colA / colB are the two orientation columns in the format's order
 *  (dip-dipdir: Dip, Dip dir; strike: Strike, Dip; trend-plunge: Trend, Plunge). */
export interface Mapping { convention: Convention; colA: number; colB: number; weightCol?: number | null; traverseCol?: number | null }
export interface Plane { dip: number; dipDir: number; quantity: number; row: number; extra: string[]; traverse?: string }
/** A row that was skipped or needs a look. `code` is stable and meant for the app to word
 *  (in any language); `reason` is a plain English description. */
export type ParseIssueCode = "not-number" | "out-of-range" | "scanline-orientation" | "measurement-orientation" | "unknown-scanline";
export interface ParseIssue { row: number; code: ParseIssueCode; reason: string; values?: (string | number)[] }

export function parseTable(text: string): Table {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim() !== "" && !l.trim().startsWith("#"));
  if (!lines.length) return { header: null, rows: [] };
  const sample = lines.slice(0, 20).join("\n");
  const counts = [",", ";", "\t"].map((d) => [d, sample.split(d).length - 1] as [string, number]);
  counts.sort((a, b) => b[1] - a[1]);
  const delim = counts[0][1] > 0 ? counts[0][0] : null;
  const split = (l: string) => (delim ? l.split(delim) : l.trim().split(/\s+/)).map((c) => c.trim().replace(/^"(.*)"$/, "$1"));
  return tableFromRows(lines.map(split));
}

/** Build a Table from already-split rows (e.g. a spreadsheet sheet). Empty rows are dropped. */
export function tableFromRows(rows0: string[][]): Table {
  const rows = rows0.map((r) => r.map((c) => (c ?? "").trim())).filter((r) => r.some((c) => c !== ""));
  if (!rows.length) return { header: null, rows: [] };
  const isNum = (c: string) => c !== "" && !isNaN(Number(c.replace(",", ".")));
  const firstNumeric = rows[0].filter(isNum).length;
  const header = firstNumeric < Math.min(2, rows[0].length) ? rows[0] : null;
  return { header, rows: header ? rows.slice(1) : rows };
}

/** Scanline field template: sheet "scanlines" (scanline_id, trend, plunge, ...) and
 *  sheet "measurements" (scanline_id, dip_direction, dip, ...). */
export interface ScanlineImport {
  scanlines: { id: string; trend: number; plunge: number; length?: number }[];
  planes: Plane[]; issues: ParseIssue[];
}
export function parseScanlineTemplate(sheets: { name: string; rows: string[][] }[]): ScanlineImport | null {
  const find = (n: string) => sheets.find((s) => s.name.trim().toLowerCase() === n);
  const sl = find("scanlines"), ms = find("measurements");
  if (!sl || !ms) return null;
  const T1 = tableFromRows(sl.rows), T2 = tableFromRows(ms.rows);
  if (!T1.header || !T2.header) return null;
  const col = (t: Table, n: string) => t.header!.findIndex((h) => h.trim().toLowerCase() === n);
  const [sid, str, spl, slen] = ["scanline_id", "trend", "plunge", "length_m"].map((n) => col(T1, n));
  const [mid, mdd, mdip] = ["scanline_id", "dip_direction", "dip"].map((n) => col(T2, n));
  if (sid < 0 || str < 0 || spl < 0 || mid < 0 || mdd < 0 || mdip < 0) return null;
  const num = (x: string | undefined) => (x === undefined || x === "" ? NaN : Number(x.replace(",", ".")));
  const issues: ParseIssue[] = [];
  const scanlines: ScanlineImport["scanlines"] = [];
  T1.rows.forEach((r, i) => {
    const id = (r[sid] ?? "").trim(), t = num(r[str]), pl = num(r[spl]);
    if (!id) return;
    if (!isFinite(t) || !isFinite(pl) || t < 0 || t > 360 || pl < -90 || pl > 90) { issues.push({ row: i + 2, code: "scanline-orientation", reason: `scanlines: invalid trend/plunge for ${id}`, values: [id] }); return; }
    const L = slen >= 0 ? num(r[slen]) : NaN;
    scanlines.push({ id, trend: t, plunge: pl, ...(isFinite(L) ? { length: L } : {}) });
  });
  const ids = new Set(scanlines.map((s) => s.id));
  const planes: Plane[] = [];
  T2.rows.forEach((r, i) => {
    const row = i + 2, id = (r[mid] ?? "").trim(), dd = num(r[mdd]), dip = num(r[mdip]);
    if (!isFinite(dd) || !isFinite(dip) || dip < 0 || dip > 90 || dd < 0 || dd > 360) { issues.push({ row, code: "measurement-orientation", reason: "measurements: invalid dip/dip direction" }); return; }
    if (id && !ids.has(id)) issues.push({ row, code: "unknown-scanline", reason: `measurements: ${id} is not in the scanlines sheet (counted as spot)`, values: [id] });
    planes.push({ dip, dipDir: dd % 360, quantity: 1, row, extra: [], ...(id && ids.has(id) ? { traverse: id } : {}) });
  });
  return { scanlines, planes, issues };
}

/** Best-guess format and columns from header names; falls back to columns 0/1 as dip/dipdir. */
export function guessMapping(t: Table): Mapping {
  const raw = (t.header ?? []).map((s) => s.toLowerCase());
  const h = raw.map((s) => s.replace(/[\s_\-./()]/g, ""));
  const find = (re: RegExp, not = -1) => h.findIndex((s, i) => i !== not && re.test(s));
  const wt = find(/^(quantity|qty|weight|count|n)$/);
  const w = wt >= 0 ? wt : null;
  const trv = find(/^(traverse|traverseid|trav|travid)$/);
  const tv = trv >= 0 ? trv : null;
  const dd = find(/^(dipdir|dipdirection|dd|ddir|dipazimuth|azimuth|az)/);
  const dip = find(/^dip(angle)?$/, dd);
  const strike = find(/^(strike|str|rhr|lhr)/);
  const trend = find(/^(trend|tr)$|^trend/);
  const plunge = find(/^(plunge|pl)$|^plunge/);
  if (dd >= 0 && dip >= 0) return { convention: "dip-dipdir", colA: dip, colB: dd, weightCol: w, traverseCol: tv };
  if (strike >= 0 && dip >= 0) {
    const left = /left|lhr/.test(h[strike]);
    return { convention: left ? "strike-lhr-dip" : "strike-rhr-dip", colA: strike, colB: dip, weightCol: w, traverseCol: tv };
  }
  if (trend >= 0 && plunge >= 0) return { convention: "trend-plunge", colA: trend, colB: plunge, weightCol: w, traverseCol: tv };
  return { convention: "dip-dipdir", colA: 0, colB: 1, weightCol: null };
}

export function toPlanes(t: Table, m: Mapping): { planes: Plane[]; issues: ParseIssue[] } {
  const planes: Plane[] = [], issues: ParseIssue[] = [];
  const num = (s: string | undefined) => (s === undefined || s === "" ? NaN : Number(s.replace(",", ".")));
  t.rows.forEach((r, i) => {
    const rowNo = i + 1 + (t.header ? 1 : 0);
    const a = num(r[m.colA]), b = num(r[m.colB]);
    if (!isFinite(a) || !isFinite(b)) { issues.push({ row: rowNo, code: "not-number", reason: "not a number" }); return; }
    const pl = formatToPlane(a, b, m.convention);
    if (!pl) { issues.push({ row: rowNo, code: "out-of-range", reason: `${a}, ${b} is out of range for this format`, values: [a, b] }); return; }
    const q = m.weightCol != null ? num(r[m.weightCol]) : 1;
    const tv = m.traverseCol != null ? (r[m.traverseCol] ?? "").trim() : "";
    planes.push({ ...pl, quantity: isFinite(q) && q > 0 ? q : 1, row: rowNo, ...(tv ? { traverse: tv } : {}),
      extra: r.filter((_, k) => k !== m.colA && k !== m.colB && k !== m.weightCol && k !== m.traverseCol) });
  });
  return { planes, issues };
}
